import React, { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ViewContext, TextInput } from 'components/lib';
import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  GridViewIcon,
  ListViewIcon,
  RefreshIcon,
  VerticalDotsIcon,
} from 'components/icons';
import { formatBytesToMbLabel } from '../../utils/formatBytes';

const RefreshIconComponent = RefreshIcon as React.ComponentType<any>;
const GridViewIconComponent = GridViewIcon as React.ComponentType<any>;
const ListViewIconComponent = ListViewIcon as React.ComponentType<any>;
const VerticalDotsIconComponent = VerticalDotsIcon as React.ComponentType<any>;
const DeleteIconComponent = DeleteIcon as React.ComponentType<any>;
const DownloadIconComponent = DownloadIcon as React.ComponentType<any>;
const EditIconComponent = EditIcon as React.ComponentType<any>;

export type GridFileItem = {
  id: number | string;
  name?: string | null;
  type?: string;
  kind?: string;
  status?: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
  contentType?: string | null;
  size?: number | null;
  downloadUrl?: string;
  [key: string]: any;
};

type TypeMetadata = {
  icon: React.ElementType;
  label: string;
};

type GridFilesPanelProps = {
  title: string;
  currentGridId: number | string | null;
  listEmptyMessage: string;
  filteredEmptyMessage: string;
  dateLabel: string;
  loadFiles: (gridId: string) => Promise<GridFileItem[]>;
  loadFileDetail: (gridId: string, fileId: string) => Promise<GridFileItem>;
  renameFile: (gridId: string, fileId: string, payload: { name?: string }) => Promise<GridFileItem>;
  downloadFile: (gridId: string, file: GridFileItem, detail?: GridFileItem) => Promise<void>;
  deleteFile?: (gridId: string, fileId: string) => Promise<unknown>;
  deleteLabel?: string;
  canDownload?: (file?: GridFileItem | null) => boolean;
  getFileType?: (file?: GridFileItem | null) => string;
  typeMetadata?: Record<string, TypeMetadata>;
  headerActions?: ReactNode;
  reloadKey?: number;
  showDeletedFiles?: boolean;
  extraColumnLabel?: string;
  getExtraColumnValue?: (file: GridFileItem) => ReactNode;
};

const defaultGetFileType = (file?: GridFileItem | null) => file?.type ?? file?.kind ?? '';

const getTypeIcon = (type?: string, typeMetadata?: Record<string, TypeMetadata>) => {
  const IconComponent = type ? (typeMetadata?.[type]?.icon ?? InsertDriveFileIcon) : InsertDriveFileIcon;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
      }}
    >
      <IconComponent className="action-icons exported-files-icon" />
    </Box>
  );
};

const formatType = (type?: string, typeMetadata?: Record<string, TypeMetadata>) => {
  if (!type) {
    return '';
  }

  const label = typeMetadata?.[type]?.label;

  if (label) {
    return label;
  }

  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getFileDateLabel = (file?: GridFileItem | null) => {
  if (!file) {
    return '—';
  }

  if (file.status === 'EXPORTING') {
    return 'exporting...';
  }

  if (!file.createdAt) {
    return '—';
  }

  return new Date(file.createdAt).toLocaleString();
};

const GridFilesPanel: React.FC<GridFilesPanelProps> = ({
  title,
  currentGridId,
  listEmptyMessage,
  filteredEmptyMessage,
  dateLabel,
  loadFiles,
  loadFileDetail,
  renameFile,
  downloadFile,
  deleteFile,
  deleteLabel = 'Delete',
  canDownload = () => true,
  getFileType = defaultGetFileType,
  typeMetadata,
  headerActions,
  reloadKey,
  showDeletedFiles = true,
  extraColumnLabel,
  getExtraColumnValue,
}) => {
  const context = useContext(ViewContext);
  const titleId = title.toLowerCase().replace(/\s+/g, '-');

  const [files, setFiles] = useState<GridFileItem[]>([]);
  const [willBeUpdatedFile, setWillBeUpdatedFile] = useState<GridFileItem | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<GridFileItem | null>(null);
  const [isGridView, setIsGridView] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActionsFile, setSelectedActionsFile] = useState<GridFileItem | null>(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  const getFiles = useCallback(async () => {
    if (!currentGridId) {
      setFiles([]);
      return;
    }

    try {
      const summaries = await loadFiles(String(currentGridId));
      const normalizedSummaries = Array.isArray(summaries) ? summaries : [];

      const details = await Promise.allSettled(
        normalizedSummaries.map(async (fileSummary) => {
          const detail = await loadFileDetail(String(currentGridId), String(fileSummary.id));
          return detail ? { ...fileSummary, ...detail } : fileSummary;
        })
      );

      const hydratedFiles = details.map((result, index) =>
        result.status === 'fulfilled' ? result.value : normalizedSummaries[index]
      );

      const rejectedDetails = details.filter((result) => result.status === 'rejected');

      if (rejectedDetails.length > 0) {
        console.error(`Unable to load one or more ${title.toLowerCase()} details:`, rejectedDetails);
      }

      setFiles(hydratedFiles);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [currentGridId, loadFileDetail, loadFiles, title]);

  useEffect(() => {
    getFiles();
  }, [getFiles, reloadKey]);

  const handleOpenUpdateModal = () => setOpenUpdateModal(true);
  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
    setWillBeUpdatedFile(null);
    setSelectedFile(null);
  };

  const handleInputsChange = (name: string, value: any) => {
    setWillBeUpdatedFile((prevState) => (prevState ? { ...prevState, [name]: value } : prevState));
  };

  const handleRenameFile = (file: GridFileItem) => {
    setSelectedFile(file);
    setWillBeUpdatedFile({ ...file });
    handleOpenUpdateModal();
  };

  const handleUpdateFileName = async () => {
    if (!selectedFile?.id || !currentGridId) {
      return;
    }

    try {
      const response = await renameFile(String(currentGridId), String(selectedFile.id), {
        name: willBeUpdatedFile?.name ?? undefined,
      });

      if (response) {
        setFiles((prevFiles) => prevFiles.map((file) => (file.id === response.id ? response : file)));
        setSelectedFile((prevSelected) => (prevSelected?.id === response.id ? response : prevSelected));
      }

      handleCloseUpdateModal();
      context.notification.show('File name was updated', 'success', true, 'toast');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeleteFile = async (file?: GridFileItem | null) => {
    if (!file || !deleteFile) {
      return;
    }

    const fileName = file.name ?? 'file';
    const deleteTitle = `${deleteLabel} ${fileName}`;
    const deleteText = `Are you sure you want to ${deleteLabel.toLowerCase()} ${fileName}?`;

    context.modal.show(
      {
        title: (
          <Tooltip title={deleteTitle} placement="top">
            <span className="file-delete-modal__ellipsis">{deleteTitle}</span>
          </Tooltip>
        ),
        form: {},
        buttonText: deleteLabel,
        text: (
          <Tooltip title={deleteText} placement="top">
            <span className="file-delete-modal__ellipsis">{deleteText}</span>
          </Tooltip>
        ),
        destructive: true,
        customSubmit: () => deleteFile(String(currentGridId), String(file.id)),
      },
      () => {
        setFiles((prevFiles) => prevFiles.filter((currentFile) => currentFile.id !== file.id));
        context.notification.show(`${fileName} was deleted`, 'success', true, 'toast');
      }
    );
  };

  const handleDownloadCurrentFile = async (file?: GridFileItem | null) => {
    if (!file) {
      return;
    }

    try {
      const detail = await loadFileDetail(String(currentGridId), String(file.id));

      if (detail) {
        setFiles((prevFiles) =>
          prevFiles.map((currentFile) => (currentFile.id === detail.id ? { ...currentFile, ...detail } : currentFile))
        );
        setSelectedFile((prevSelected) =>
          prevSelected?.id === detail.id ? { ...prevSelected, ...detail } : prevSelected
        );
      }

      await downloadFile(String(currentGridId), file, detail);
    } catch (error) {
      console.error('Error:', error);
      context.notification.show(`Unable to download ${title.toLowerCase().slice(0, -1)}`, 'error', true, 'toast');
    }
  };

  const availableTypeOptions = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(
        (files ?? [])
          .filter((file) => showDeletedFiles || !file.deletedAt)
          .map((file) => getFileType(file))
          .filter((type): type is string => Boolean(type))
      )
    );

    return uniqueTypes.map((type) => ({
      value: type,
      label: formatType(type, typeMetadata),
    }));
  }, [files, getFileType, showDeletedFiles, typeMetadata]);

  const filteredFiles = useMemo(() => {
    const visibleFiles = showDeletedFiles ? files : files.filter((file) => !file.deletedAt);

    if (!selectedTypes.length) return visibleFiles;

    return visibleFiles.filter((file) => {
      const type = getFileType(file);
      return Boolean(type) && selectedTypes.includes(type);
    });
  }, [files, getFileType, selectedTypes, showDeletedFiles]);

  const handleTypeFilterChange = (event: React.ChangeEvent<{ value: unknown }> | any) => {
    const { value } = event.target;
    setSelectedTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const getFileTypeLabel = (file?: GridFileItem | null) => {
    if (!file) {
      return '';
    }

    const typeLabel = formatType(getFileType(file), typeMetadata);
    const sizeLabel = formatBytesToMbLabel(file.size);

    return sizeLabel ? `${typeLabel} • ${sizeLabel}` : typeLabel;
  };

  const filtersApplied = selectedTypes.length > 0;
  const toggleViewMode = () => setIsGridView((prev) => !prev);
  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, file: GridFileItem) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedActionsFile(file);
  };
  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
    setSelectedActionsFile(null);
  };

  return (
    <>
      {currentGridId && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 5, mb: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <h2 style={{ margin: 0, fontWeight: 'bold' }}>{title}</h2>
                <IconButton aria-label={`refresh ${title.toLowerCase()}`} size="small" onClick={getFiles}>
                  <RefreshIconComponent />
                </IconButton>
                <IconButton aria-label={`toggle ${title.toLowerCase()} view`} size="small" onClick={toggleViewMode}>
                  {isGridView ? <ListViewIconComponent /> : <GridViewIconComponent />}
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {headerActions}
                <FormControl
                  className="files-filter-select"
                  sx={{ minWidth: 220 }}
                  size="small"
                  disabled={!availableTypeOptions.length}
                >
                  <InputLabel id={`${titleId}-type-filter-label`}>Filter by type</InputLabel>
                  <Select
                    labelId={`${titleId}-type-filter-label`}
                    multiple
                    value={selectedTypes}
                    onChange={handleTypeFilterChange}
                    input={<OutlinedInput label="Filter by type" />}
                    renderValue={(selected) => {
                      if (!selected.length) {
                        return '';
                      }

                      if (selected.length === 1) {
                        return formatType(selected[0], typeMetadata);
                      }

                      return `${selected.length} filters`;
                    }}
                  >
                    {availableTypeOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox checked={selectedTypes.includes(value)} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {filteredFiles.length ? (
              isGridView ? (
                <Grid container spacing={2}>
                  {filteredFiles.map((file) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={file.id}>
                      <Paper
                        className="stat"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          px: 2,
                          py: 1,
                          backgroundColor: 'var(--theme-bg-color)',
                          color: file.deletedAt ? 'text.disabled' : undefined,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            {getTypeIcon(getFileType(file), typeMetadata)}
                            <Box
                              component="span"
                              sx={{
                                fontSize: '0.8rem !important',
                                fontWeight: 400,
                                color: file.deletedAt ? 'inherit' : '#5e5e5e',
                                letterSpacing: 0.15,
                                lineHeight: 1.05,
                              }}
                            >
                              {getFileTypeLabel(file)}
                            </Box>
                          </Box>
                          <IconButton
                            sx={{ right: -10 }}
                            aria-label={`Actions for ${file.name}`}
                            size="small"
                            onClick={(event) => handleOpenActionsMenu(event, file)}
                          >
                            <VerticalDotsIconComponent />
                          </IconButton>
                        </Box>
                        <Tooltip title={file.name ?? ''} placement="top">
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: 1,
                              display: 'block',
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              flex: 1,
                              textAlign: 'left',
                              fontSize: '1.2rem !important',
                            }}
                          >
                            {file.name}
                          </Box>
                        </Tooltip>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: file.deletedAt ? 'inherit' : '#5e5e5e',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ fontSize: '0.8rem !important', lineHeight: 1.1, letterSpacing: 0.1 }}
                          >
                            {dateLabel}
                          </Box>
                          <Box
                            component="span"
                            sx={{ fontSize: '0.8rem !important', lineHeight: 1.1, letterSpacing: 0.1 }}
                          >
                            {getFileDateLabel(file)}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <TableContainer component={Paper} className="stat">
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '35%' }} />
                      <col style={{ width: extraColumnLabel && getExtraColumnValue ? '20%' : '30%' }} />
                      {extraColumnLabel && getExtraColumnValue && <col style={{ width: '10%' }} />}
                      <col style={{ width: '25%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgb(146 151 179 / 25%)' }}>
                        <TableCell>File</TableCell>
                        <TableCell>Type</TableCell>
                        {extraColumnLabel && getExtraColumnValue && <TableCell>{extraColumnLabel}</TableCell>}
                        <TableCell>{dateLabel} Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredFiles.map((file) => (
                        <TableRow
                          key={file.id}
                          hover
                          sx={{
                            color: file.deletedAt ? 'text.disabled' : undefined,
                            '& td': { color: 'inherit' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                              {getTypeIcon(getFileType(file), typeMetadata)}
                              <Box
                                component="span"
                                title={file.name ?? ''}
                                sx={{
                                  fontWeight: 500,
                                  color: file.deletedAt ? 'text.disabled' : 'inherit',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {file.name}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{getFileTypeLabel(file)}</TableCell>
                          {extraColumnLabel && getExtraColumnValue && (
                            <TableCell>{getExtraColumnValue(file)}</TableCell>
                          )}
                          <TableCell>{getFileDateLabel(file)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              aria-label={`Actions for ${file.name}`}
                              size="small"
                              onClick={(event) => handleOpenActionsMenu(event, file)}
                            >
                              <VerticalDotsIconComponent />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : (
              <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
                {filtersApplied ? filteredEmptyMessage : listEmptyMessage}
              </Alert>
            )}

            <Menu
              anchorEl={actionsAnchorEl}
              keepMounted
              open={Boolean(actionsAnchorEl)}
              onClose={handleCloseActionsMenu}
              className="actions-menu"
            >
              <MenuItem
                className="actions-menu-item"
                disabled={!canDownload(selectedActionsFile)}
                onClick={() => {
                  if (selectedActionsFile) {
                    handleDownloadCurrentFile(selectedActionsFile);
                  }
                  handleCloseActionsMenu();
                }}
              >
                <Tooltip title={`Download ${selectedActionsFile?.name ?? 'file'}`}>
                  <IconButton disabled={!canDownload(selectedActionsFile)}>
                    <DownloadIconComponent />
                  </IconButton>
                </Tooltip>
              </MenuItem>
              <MenuItem
                className="actions-menu-item"
                onClick={() => {
                  if (selectedActionsFile) {
                    handleRenameFile(selectedActionsFile);
                  }
                  handleCloseActionsMenu();
                }}
              >
                <Tooltip title={`Rename ${selectedActionsFile?.name ?? 'file'}`}>
                  <IconButton>
                    <EditIconComponent />
                  </IconButton>
                </Tooltip>
              </MenuItem>
              {deleteFile && (
                <MenuItem
                  className="actions-menu-item"
                  onClick={() => {
                    if (selectedActionsFile) {
                      handleDeleteFile(selectedActionsFile);
                    }
                    handleCloseActionsMenu();
                  }}
                >
                  <Tooltip title={`Delete ${selectedActionsFile?.name ?? 'file'}`}>
                    <IconButton aria-label="delete" color="error">
                      <DeleteIconComponent />
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </>
      )}

      <Modal
        open={openUpdateModal}
        onClose={handleCloseUpdateModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="custom-modal"
      >
        <Box className="file-rename-modal">
          <TextInput
            label="File Name"
            name="name"
            value={willBeUpdatedFile?.name ?? ''}
            onChange={handleInputsChange}
            width="100%"
          />
          <Box className="file-rename-modal__actions">
            <Button variant="contained" className="modal-no-btn" onClick={handleCloseUpdateModal}>
              Cancel
            </Button>
            <Button variant="contained" className="modal-yes-btn" onClick={handleUpdateFileName}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default GridFilesPanel;
